import { AgsTranslationsService } from "ags/services/ags-translation";
import { CanvasService } from "appworks/graphics/canvas/canvas-service";
import { Orientation } from "appworks/graphics/canvas/orientation";
import { GraphicsService } from "appworks/graphics/graphics-service";
import { Layers } from "appworks/graphics/layers/layers";
import { Scene } from "appworks/graphics/layers/scene";
import { HTMLText } from "appworks/graphics/pixi/html-text";
import { RenderOrientation } from "appworks/graphics/pixi/render-orientation";
import { Services } from "appworks/services/services";
import { TranslationsService } from "appworks/services/translations/translations-service";
import { Contract } from "appworks/utils/contracts/contract";
import { Sequence } from "appworks/utils/contracts/sequence";
import { deviceInfo } from "appworks/utils/device-info";
import { gameiomModel } from "gameiom/model/gameiom-model";
import { PaytableComponent, PaytableComponentConfig } from "slotworks/components/paytable/paytable-component";

export class AgsPaytableComponent extends PaytableComponent {
    constructor(config?: Partial<PaytableComponentConfig>) {
        super(config);

        Layers.get("PaytableContent").onSceneEnter.add(() => {
            const rtpText = Layers.get("PaytableContent").getText("pg6in3");
            if (rtpText) {
                let rtpNumber: string | number = Number(gameiomModel.rtp);
                if (!isNaN(rtpNumber)) {
                    rtpNumber = rtpNumber.toFixed(2);
                } else {
                    rtpNumber = gameiomModel.rtp;
                }
                rtpText.text = Services.get(TranslationsService).get("game_info_3", { rtp: rtpNumber });
            }

            if (gameiomModel.isSocial || gameiomModel.displayCurrency) {
                const rulesText = Layers.get("PaytableContent").getText("game_rules");
                const tknTranslationExists = Services.get(AgsTranslationsService).exists("game_rules_tkn");
                if (rulesText && tknTranslationExists) {
                    rulesText.text = Services.get(AgsTranslationsService).get("game_rules_tkn");
                }
            }
        });
    }

    // NOTE(steven): Any text that is not within the bounds of the layer's 
    // container will be cut off in the prerendered image.
    public prerenderHtmlText(): Contract<void> {
        const cachedOrientation = deviceInfo.getOrientation();

        const paytableLayer: Layers = Layers.get("PaytableContent");
        paytableLayer.onSceneEnter.active = false;
        paytableLayer.onSceneExit.active = false;
        
        const sequence: Array<() => Contract<void>> = [];
        for (let page = 1; paytableLayer.hasScene(`page_${page}`); page++) {
            const sceneName = `page_${page}`;
            const scene: Scene = paytableLayer.getScene(sceneName);

            sequence.push(() => new Contract<void>((resolve) => {
                this.prerenderScene(paytableLayer, scene, Orientation.PORTRAIT);
                this.prerenderScene(paytableLayer, scene, Orientation.LANDSCAPE);
                
                // Remove all html text configs so we just show the prerendered image
                Object.keys(scene.config.text)
                    .filter((key) => key.split("-")[0] === "html")
                    .forEach((key) => delete scene.config.text[key]);

                resolve();
            }));
        }

        sequence.push(() => Contract.wrap(() => {
            RenderOrientation.orientation = cachedOrientation;
            paytableLayer.jumpToScene("default");
            paytableLayer.onSceneEnter.active = true;
            paytableLayer.onSceneExit.active = true;
        }));
        
        return new Sequence(sequence);
    }

    protected prerenderScene(layer: Layers, scene: Scene, orientation: Orientation): void {
        const ids: string[] = Object.keys(scene.config.text);
        const hasHtmlText: boolean = ids.some((id) => id.split("-")[0] === "html");
        if (!hasHtmlText) {
            return;
        }

        RenderOrientation.orientation = orientation;
        layer.jumpToScene(scene.name, true, true);
        
        const canvas = Services.get(CanvasService);
        const texture = PIXI.RenderTexture.create({ 
            width: canvas.viewport[orientation].width, 
            height: canvas.viewport[orientation].height,
            resolution: canvas.resolution
        });
        
        for (const id of ids) {
            const text: HTMLText = layer.getHTMLText(id.split("-")[1]);
            if (text != null) {
                canvas.renderer.render(text, texture, false);
            }
        }
        
        const textureName = `prerendered_${layer.id}_${scene.name}_${orientation}`;
        Services.get(GraphicsService).addTexture(textureName, texture);
        scene.config.sprites[textureName] = {
            [orientation]: {
                x: 0,
                y: 0,
                width: canvas.viewport[orientation].width,
                height: canvas.viewport[orientation].height,
            }
        }
    }
}

